import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Search,
  Plus,
  Users,
  Phone,
  Mail,
  Edit,
  X,
  Check,
  User,
  CreditCard,
  UserCheck,
  UserX,
  Trash2,
  Link2,
  AlertTriangle,
  CheckCircle,
  Loader2,
  UserPlus,
  Link
} from 'lucide-react';
import MetricasVendedores from '../components/MetricasVendedores';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendedoresList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  
  // Modal crear vendedor desde usuario
  const [showModal, setShowModal] = useState(false);
  const [editandoVendedor, setEditandoVendedor] = useState(null);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [searchUsuario, setSearchUsuario] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    documento: '',
    telefono: '',
    correo: '',
    estado: 'activo'
  });
  const [guardando, setGuardando] = useState(false);
  const [confirmarRolDiferente, setConfirmarRolDiferente] = useState(false);
  const [modoCreacion, setModoCreacion] = useState('usuario'); // 'usuario' | 'manual'
  
  // Modal vincular vendedor existente
  const [showModalVincular, setShowModalVincular] = useState(false);
  const [vendedorAVincular, setVendedorAVincular] = useState(null);

  // Verificar permisos
  const canManage = user?.role === 'gerencia' || user?.role === 'admin';

  const fetchVendedores = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (search) params.search = search;
      
      const response = await axios.get(`${API_URL}/api/vendedores`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendedores(response.data.vendedores || []);
    } catch (error) {
      toast.error('Error al cargar vendedores');
    } finally {
      setLoading(false);
    }
  }, [search, filtroEstado]);

  useEffect(() => {
    fetchVendedores();
  }, [fetchVendedores]);

  const fetchUsuariosDisponibles = async () => {
    setLoadingUsuarios(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/vendedores/usuarios-disponibles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsuariosDisponibles(response.data.usuarios || []);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchVendedores();
  };

  const abrirModalCrear = () => {
    setEditandoVendedor(null);
    setUsuarioSeleccionado(null);
    setSearchUsuario('');
    setConfirmarRolDiferente(false);
    setModoCreacion('usuario');
    setFormData({
      nombre: '',
      documento: '',
      telefono: '',
      correo: '',
      estado: 'activo'
    });
    setShowModal(true);
    fetchUsuariosDisponibles();
  };

  const abrirModalEditar = (vendedor) => {
    setEditandoVendedor(vendedor);
    setModoCreacion('manual');
    setFormData({
      nombre: vendedor.nombre || '',
      documento: vendedor.documento || '',
      telefono: vendedor.telefono || '',
      correo: vendedor.correo || '',
      estado: vendedor.estado || 'activo'
    });
    setShowModal(true);
  };

  const abrirModalVincular = (vendedor) => {
    setVendedorAVincular(vendedor);
    setUsuarioSeleccionado(null);
    setSearchUsuario('');
    setShowModalVincular(true);
    fetchUsuariosDisponibles();
  };

  const seleccionarUsuario = (usuario) => {
    if (usuario.ya_es_vendedor) {
      toast.error(`Este usuario ya está registrado como vendedor (${usuario.vendedor_codigo})`);
      return;
    }
    setUsuarioSeleccionado(usuario);
    setFormData({
      nombre: usuario.username || '',
      documento: '',
      telefono: usuario.phone || '',
      correo: usuario.email || '',
      estado: 'activo'
    });
    
    // Verificar si necesita confirmación por rol diferente
    if (!['vendedor', 'admin', 'gerencia'].includes(usuario.role)) {
      setConfirmarRolDiferente(false); // Reset, el usuario debe confirmarlo
    }
  };

  const guardarVendedor = async () => {
    setGuardando(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editandoVendedor) {
        // Actualizar vendedor existente
        const dataToSend = {
          ...formData,
          correo: formData.correo || null
        };
        await axios.put(`${API_URL}/api/vendedores/${editandoVendedor.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vendedor actualizado');
      } else if (modoCreacion === 'usuario' && usuarioSeleccionado) {
        // Crear desde usuario
        const dataToSend = {
          usuario_id: usuarioSeleccionado.id,
          documento: formData.documento || null,
          telefono: formData.telefono || null,
          confirmar_rol_diferente: confirmarRolDiferente
        };
        const response = await axios.post(`${API_URL}/api/vendedores/desde-usuario`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(response.data.mensaje || 'Vendedor creado exitosamente');
      } else {
        // Crear manualmente (modo legacy)
        if (!formData.nombre) {
          toast.error('El nombre es obligatorio');
          setGuardando(false);
          return;
        }
        const dataToSend = {
          ...formData,
          correo: formData.correo || null
        };
        const response = await axios.post(`${API_URL}/api/vendedores`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Vendedor ${response.data.id_vendedor} creado exitosamente`);
      }
      
      setShowModal(false);
      fetchVendedores();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Error al guardar vendedor';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setGuardando(false);
    }
  };

  const vincularVendedor = async () => {
    if (!usuarioSeleccionado || !vendedorAVincular) return;
    
    setGuardando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/vendedores/vincular-usuario`, {
        vendedor_id: vendedorAVincular.id,
        usuario_id: usuarioSeleccionado.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.mensaje || 'Vendedor vinculado exitosamente');
      setShowModalVincular(false);
      fetchVendedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vincular vendedor');
    } finally {
      setGuardando(false);
    }
  };

  const toggleEstado = async (vendedor) => {
    const nuevoEstado = vendedor.estado === 'activo' ? 'inactivo' : 'activo';
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/vendedores/${vendedor.id}`, 
        { estado: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Vendedor ${nuevoEstado === 'activo' ? 'activado' : 'desactivado'}`);
      fetchVendedores();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const eliminarVendedor = async (vendedor) => {
    if (!window.confirm(`¿Estás seguro de eliminar al vendedor "${vendedor.nombre}" (${vendedor.id_vendedor})?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/vendedores/${vendedor.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendedor eliminado exitosamente');
      fetchVendedores();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al eliminar vendedor. Puede tener fichas o cotizaciones asociadas.');
    }
  };

  // Filtrar usuarios por búsqueda
  const usuariosFiltrados = usuariosDisponibles.filter(u => {
    const term = searchUsuario.toLowerCase();
    return (
      u.username?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term) ||
      u.phone?.includes(term)
    );
  });

  if (!canManage) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white">
        <div className="text-center">
          <UserX className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Acceso Denegado</h2>
          <p className="text-zinc-400">Solo Gerencia puede administrar vendedores</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-serif">Vendedores Autorizados</h1>
                <p className="text-sm text-zinc-400">{vendedores.length} vendedores registrados</p>
              </div>
            </div>
            
            <button
              onClick={abrirModalCrear}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-medium rounded-lg transition-colors"
              data-testid="crear-vendedor-btn"
            >
              <UserPlus className="w-4 h-4" />
              Nuevo Vendedor
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Métricas de Rendimiento Comercial */}
        {(user?.role === 'gerencia' || user?.role === 'admin') && (
          <section className="mb-8">
            <MetricasVendedores userRole={user?.role} soloRanking={false} />
          </section>
        )}

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, código o documento..."
                className="w-full pl-12 pr-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl focus:border-[#D4AF37] focus:outline-none"
                data-testid="search-vendedores"
              />
            </div>
          </form>
          
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl focus:border-[#D4AF37] focus:outline-none"
          >
            <option value="">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
        </div>

        {/* Lista de vendedores */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
          </div>
        ) : vendedores.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">No se encontraron vendedores</p>
            <button
              onClick={abrirModalCrear}
              className="mt-4 text-[#D4AF37] hover:underline"
            >
              Crear primer vendedor
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendedores.map(vendedor => (
              <div
                key={vendedor.id}
                className={`bg-zinc-900 border rounded-xl p-5 transition-colors ${
                  vendedor.estado === 'activo' 
                    ? 'border-zinc-800 hover:border-zinc-700' 
                    : 'border-red-900/30 bg-zinc-900/50'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      vendedor.estado === 'activo' 
                        ? vendedor.usuario_id ? 'bg-emerald-500/20' : 'bg-[#D4AF37]/20'
                        : 'bg-zinc-700/50'
                    }`}>
                      {vendedor.usuario_id ? (
                        <Link2 className={`w-6 h-6 ${vendedor.estado === 'activo' ? 'text-emerald-400' : 'text-zinc-500'}`} />
                      ) : (
                        <User className={`w-6 h-6 ${vendedor.estado === 'activo' ? 'text-[#D4AF37]' : 'text-zinc-500'}`} />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{vendedor.nombre}</h3>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          vendedor.estado === 'activo' 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {vendedor.id_vendedor}
                        </span>
                        {vendedor.usuario_id && (
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                            Vinculado
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    {!vendedor.usuario_id && (
                      <button
                        onClick={() => abrirModalVincular(vendedor)}
                        className="p-2 hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Vincular a usuario"
                        data-testid={`vincular-vendedor-${vendedor.id}`}
                      >
                        <Link className="w-4 h-4 text-blue-400" />
                      </button>
                    )}
                    <button
                      onClick={() => abrirModalEditar(vendedor)}
                      className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                      title="Editar"
                      data-testid={`editar-vendedor-${vendedor.id}`}
                    >
                      <Edit className="w-4 h-4 text-zinc-400" />
                    </button>
                    <button
                      onClick={() => toggleEstado(vendedor)}
                      className={`p-2 rounded-lg transition-colors ${
                        vendedor.estado === 'activo' 
                          ? 'hover:bg-red-900/30' 
                          : 'hover:bg-emerald-900/30'
                      }`}
                      title={vendedor.estado === 'activo' ? 'Desactivar' : 'Activar'}
                    >
                      {vendedor.estado === 'activo' ? (
                        <UserX className="w-4 h-4 text-red-400" />
                      ) : (
                        <UserCheck className="w-4 h-4 text-emerald-400" />
                      )}
                    </button>
                    <button
                      onClick={() => eliminarVendedor(vendedor)}
                      className="p-2 hover:bg-red-900/30 rounded-lg transition-colors"
                      title="Eliminar vendedor"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  {vendedor.documento && (
                    <div className="flex items-center gap-2 text-zinc-400">
                      <CreditCard className="w-4 h-4" />
                      <span>{vendedor.documento}</span>
                    </div>
                  )}
                  {vendedor.telefono && (
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Phone className="w-4 h-4" />
                      <span>{vendedor.telefono}</span>
                    </div>
                  )}
                  {vendedor.correo && (
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{vendedor.correo}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modal Crear/Editar Vendedor */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  {editandoVendedor ? (
                    <>
                      <Edit className="w-5 h-5 text-[#D4AF37]" />
                      Editar Vendedor
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 text-[#D4AF37]" />
                      Nuevo Vendedor
                    </>
                  )}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Tabs de modo solo para nuevo vendedor */}
              {!editandoVendedor && (
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => { setModoCreacion('usuario'); setUsuarioSeleccionado(null); }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                      modoCreacion === 'usuario' 
                        ? 'bg-[#D4AF37] text-black' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    Desde Usuario del Sistema
                  </button>
                  <button
                    onClick={() => { setModoCreacion('manual'); setUsuarioSeleccionado(null); }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                      modoCreacion === 'manual' 
                        ? 'bg-[#D4AF37] text-black' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <Edit className="w-4 h-4" />
                    Crear Manualmente
                  </button>
                </div>
              )}

              {/* Modo: Desde Usuario */}
              {!editandoVendedor && modoCreacion === 'usuario' && (
                <div className="space-y-4">
                  {/* Buscador de usuarios */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Buscar Usuario</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="text"
                        value={searchUsuario}
                        onChange={(e) => setSearchUsuario(e.target.value)}
                        placeholder="Buscar por nombre, correo o teléfono..."
                        className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                        data-testid="search-usuario-vendedor"
                      />
                    </div>
                  </div>

                  {/* Lista de usuarios */}
                  <div className="max-h-64 overflow-y-auto border border-zinc-800 rounded-lg">
                    {loadingUsuarios ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
                      </div>
                    ) : usuariosFiltrados.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        No se encontraron usuarios
                      </div>
                    ) : (
                      usuariosFiltrados.map(usuario => (
                        <div
                          key={usuario.id}
                          onClick={() => seleccionarUsuario(usuario)}
                          className={`p-3 border-b border-zinc-800 last:border-0 cursor-pointer transition-colors ${
                            usuarioSeleccionado?.id === usuario.id 
                              ? 'bg-[#D4AF37]/20 border-l-2 border-l-[#D4AF37]' 
                              : usuario.ya_es_vendedor 
                                ? 'bg-zinc-800/50 cursor-not-allowed'
                                : 'hover:bg-zinc-800'
                          }`}
                          data-testid={`usuario-option-${usuario.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                usuario.ya_es_vendedor ? 'bg-zinc-700' : 'bg-zinc-800'
                              }`}>
                                {usuario.ya_es_vendedor ? (
                                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                                ) : (
                                  <User className="w-5 h-5 text-zinc-400" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-white">{usuario.username}</p>
                                <p className="text-xs text-zinc-400">{usuario.email}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                usuario.role === 'vendedor' ? 'bg-emerald-500/20 text-emerald-400' :
                                usuario.role === 'gerencia' ? 'bg-purple-500/20 text-purple-400' :
                                usuario.role === 'admin' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-zinc-700 text-zinc-400'
                              }`}>
                                {usuario.role}
                              </span>
                              {usuario.ya_es_vendedor && (
                                <p className="text-xs text-emerald-400 mt-1">
                                  Ya es vendedor ({usuario.vendedor_codigo})
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Usuario seleccionado */}
                  {usuarioSeleccionado && (
                    <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
                      <div className="flex items-center gap-3 pb-3 border-b border-zinc-700">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                        <span className="font-medium">Usuario seleccionado: {usuarioSeleccionado.username}</span>
                      </div>

                      {/* Advertencia de rol */}
                      {!['vendedor', 'admin', 'gerencia'].includes(usuarioSeleccionado.role) && (
                        <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-amber-200">
                              Este usuario tiene rol &quot;{usuarioSeleccionado.role}&quot;, no &quot;vendedor&quot;.
                            </p>
                            <label className="flex items-center gap-2 mt-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={confirmarRolDiferente}
                                onChange={(e) => setConfirmarRolDiferente(e.target.checked)}
                                className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-[#D4AF37] focus:ring-[#D4AF37]"
                              />
                              <span className="text-sm text-zinc-300">
                                Confirmo que deseo vincularlo como vendedor igualmente
                              </span>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Datos adicionales */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Documento (opcional)</label>
                          <input
                            type="text"
                            value={formData.documento}
                            onChange={(e) => setFormData(prev => ({ ...prev, documento: e.target.value }))}
                            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                            placeholder="Número de documento"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Teléfono</label>
                          <input
                            type="tel"
                            value={formData.telefono}
                            onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                            placeholder={usuarioSeleccionado.phone || "3001234567"}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Modo: Manual */}
              {(editandoVendedor || modoCreacion === 'manual') && (
                <div className="space-y-4">
                  {!editandoVendedor && (
                    <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
                      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-200">
                        Crear un vendedor manualmente no lo vincula a ningún usuario del sistema. 
                        Se recomienda usar &quot;Desde Usuario del Sistema&quot; para mantener la integridad de datos.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Nombre Completo *</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Juan Pablo Murillo"
                      data-testid="input-nombre-vendedor"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Documento / Identificación</label>
                    <input
                      type="text"
                      value={formData.documento}
                      onChange={(e) => setFormData(prev => ({ ...prev, documento: e.target.value }))}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                      placeholder="1234567890"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Teléfono</label>
                      <input
                        type="tel"
                        value={formData.telefono}
                        onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                        placeholder="3001234567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Estado</label>
                      <select
                        value={formData.estado}
                        onChange={(e) => setFormData(prev => ({ ...prev, estado: e.target.value }))}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                      >
                        <option value="activo">Activo</option>
                        <option value="inactivo">Inactivo</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Correo Electrónico</label>
                    <input
                      type="email"
                      value={formData.correo}
                      onChange={(e) => setFormData(prev => ({ ...prev, correo: e.target.value }))}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                      placeholder="vendedor@jpmsastreria.com"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={guardarVendedor}
                disabled={guardando || (modoCreacion === 'usuario' && !usuarioSeleccionado) || 
                  (modoCreacion === 'usuario' && !['vendedor', 'admin', 'gerencia'].includes(usuarioSeleccionado?.role) && !confirmarRolDiferente)}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirmar-vendedor"
              >
                {guardando ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {editandoVendedor ? 'Actualizar' : 'Crear Vendedor'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Vincular Vendedor Existente */}
      {showModalVincular && vendedorAVincular && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-blue-400" />
                  Vincular Vendedor a Usuario
                </h2>
                <button
                  onClick={() => setShowModalVincular(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Info del vendedor */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Vendedor a vincular:</p>
                <p className="font-medium text-white">{vendedorAVincular.nombre}</p>
                <p className="text-xs text-zinc-400">{vendedorAVincular.id_vendedor}</p>
              </div>

              {/* Buscador de usuarios */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Seleccionar Usuario</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={searchUsuario}
                    onChange={(e) => setSearchUsuario(e.target.value)}
                    placeholder="Buscar usuario..."
                    className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              {/* Lista de usuarios */}
              <div className="max-h-64 overflow-y-auto border border-zinc-800 rounded-lg">
                {loadingUsuarios ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
                  </div>
                ) : (
                  usuariosFiltrados.filter(u => !u.ya_es_vendedor).map(usuario => (
                    <div
                      key={usuario.id}
                      onClick={() => setUsuarioSeleccionado(usuario)}
                      className={`p-3 border-b border-zinc-800 last:border-0 cursor-pointer transition-colors ${
                        usuarioSeleccionado?.id === usuario.id 
                          ? 'bg-blue-500/20 border-l-2 border-l-blue-400' 
                          : 'hover:bg-zinc-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{usuario.username}</p>
                          <p className="text-xs text-zinc-400">{usuario.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          usuario.role === 'vendedor' ? 'bg-emerald-500/20 text-emerald-400' :
                          'bg-zinc-700 text-zinc-400'
                        }`}>
                          {usuario.role}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {usuarioSeleccionado && (
                <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                  <span className="text-sm">Vincular a: <strong>{usuarioSeleccionado.username}</strong></span>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                onClick={() => setShowModalVincular(false)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={vincularVendedor}
                disabled={guardando || !usuarioSeleccionado}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50"
              >
                {guardando ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Vinculando...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4" />
                    Vincular
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendedoresList;
